/**
 * Local golden-snapshot freshness nudge. This module is enabled only in a
 * per-engineer workstation build; Cloud Run never receives its runtime flag
 * or control endpoint. Dismissing the notice hides it for a day in this
 * browser; it comes back after that if the data is still stale.
 */
import { addHours, isBefore, parseISO } from "date-fns";
import { toast } from "sonner";

import { fetchApi } from "./api";

const ENABLED = import.meta.env.VITE_DEV_WORKSTATION_CONTROL === "true";
const TOAST_ID = "dev-workstation-outdated-data";
const HIDDEN_UNTIL_KEY = "dev-workstation-outdated-data-hidden-until";
const HIDE_HOURS = 24;
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const ACTION_POLL_INTERVAL_MS = 3 * 1000;

type ActionState = "running" | "ok" | "failed";

export interface GoldenStatus {
  snapshot_at: string | null;
  age_days: number | null;
  stale: boolean;
  action: { state: ActionState; error?: string } | null;
}

type Phase = "idle" | "offered" | "updating" | "ready" | "failed";

let phase: Phase = "idle";
let actionPoll: ReturnType<typeof setTimeout> | undefined;

/** Test-only reset for the page-lifetime toast lifecycle. */
export function resetDevWorkstationDataNudge(): void {
  phase = "idle";
  if (actionPoll) clearTimeout(actionPoll);
  actionPoll = undefined;
}

function isHidden(): boolean {
  const until = window.localStorage.getItem(HIDDEN_UNTIL_KEY);
  return until !== null && isBefore(new Date(), parseISO(until));
}

/** Hide the offer for a day; the next poll after that offers it again if still stale. */
function hideOffer(): void {
  window.localStorage.setItem(HIDDEN_UNTIL_KEY, addHours(new Date(), HIDE_HOURS).toISOString());
  phase = "idle";
}

function showOffer(status: GoldenStatus): void {
  const days = Math.max(0, status.age_days ?? 0);
  toast(`Outdated Data: your database only contains data up to ${days} days ago`, {
    id: TOAST_ID,
    duration: Infinity,
    // A swipe or close. The Dismiss button closes without calling this.
    onDismiss: () => {
      if (phase === "offered") hideOffer();
    },
    cancel: {
      label: "Dismiss",
      onClick: () => hideOffer(),
    },
    action: {
      label: "Update",
      onClick: (event) => {
        event.preventDefault();
        void startDevWorkstationReseed();
      },
    },
  });
  phase = "offered";
}

function showUpdating(): void {
  toast.loading("Updating data…", {
    id: TOAST_ID,
    description: "Reseeding this workstation from the shared golden snapshot. Leave this tab open.",
    duration: Infinity,
    action: undefined,
  });
  phase = "updating";
}

function showReady(): void {
  toast.success("New data ready", {
    id: TOAST_ID,
    description: "The reseed finished — refresh to load it.",
    duration: Infinity,
    action: { label: "Refresh", onClick: () => window.location.reload() },
  });
  phase = "ready";
}

function showFailed(message: string): void {
  toast.error("Couldn't update data", {
    id: TOAST_ID,
    description: message,
    duration: Infinity,
    action: {
      label: "Retry",
      onClick: (event) => {
        event.preventDefault();
        void startDevWorkstationReseed();
      },
    },
  });
  phase = "failed";
}

function scheduleActionPoll(): void {
  if (actionPoll) clearTimeout(actionPoll);
  actionPoll = setTimeout(() => void checkDevWorkstationDataNudge(), ACTION_POLL_INTERVAL_MS);
}

/** Fetch current local golden metadata and update the single sticky toast. */
export async function checkDevWorkstationDataNudge(): Promise<void> {
  if (!ENABLED) return;

  let status: GoldenStatus;
  try {
    status = await fetchApi("/dev-workstation/golden-status");
  } catch {
    // `dev-env reseed` deliberately restarts the backend. Keep the short poll
    // alive through that expected outage so the completion prompt is timely.
    if (phase === "updating") scheduleActionPoll();
    return;
  }

  if (status.action?.state === "running") {
    showUpdating();
    scheduleActionPoll();
    return;
  }
  if (actionPoll) clearTimeout(actionPoll);
  actionPoll = undefined;

  if (phase === "updating") {
    if (status.action?.state === "failed") {
      showFailed(status.action.error || "The reseed did not complete. Your current data is unchanged.");
      return;
    }
    if (status.action?.state === "ok") {
      showReady();
      return;
    }
  }

  if (!status.stale) {
    if (phase === "offered") toast.dismiss(TOAST_ID);
    if (phase !== "ready") phase = "idle";
    return;
  }

  if (phase === "idle" && !isHidden()) showOffer(status);
}

/** Start the reseed and immediately switch the notification into its spinner state. */
export async function startDevWorkstationReseed(): Promise<void> {
  showUpdating();
  try {
    await fetchApi("/dev-workstation/reseed", { method: "POST" });
    scheduleActionPoll();
  } catch {
    showFailed("Couldn't start the reseed. Your current data is unchanged.");
  }
}

/** Install the normal freshness poll. No-op everywhere but workstation hosts. */
export function installDevWorkstationDataNudge(): () => void {
  if (!ENABLED) return () => {};
  void checkDevWorkstationDataNudge();
  const poll = setInterval(() => void checkDevWorkstationDataNudge(), POLL_INTERVAL_MS);
  return () => {
    clearInterval(poll);
    if (actionPoll) clearTimeout(actionPoll);
  };
}
